/**
 * polylineConverter — LWPOLYLINE / POLYLINE → CadPrimitive path.
 *
 * Straight spans become `line` segments; bulged spans become exact cubic
 * `bezier` segments (the bulge is decoded to an arc via the shared
 * `bulgeToArc` math, then the arc is converted to beziers — true curves, not
 * facets). A `closed` polyline emits the final wrap span (with its own bulge)
 * and marks the path closed.
 *
 * RAW world mm coordinates (no applyOffset).
 *
 * WIDTH (group 43 `constantWidth` / per-vertex 40/41 `startWidth`/`endWidth`)
 * plots as the FILLED BAND AutoCAD draws, built by `./widePolylineBand.ts`
 * from `scene3d/adapters/geom/polylineWidthOutline.ts` — the SAME outline
 * loops `batchedLayer.ts` puts on the canvas, so the plot and the screen
 * cannot disagree about a wide polyline's shape. That module's header carries
 * the FILLMODE reference and the nonzero fill rule. A zero-width polyline is
 * unchanged: the centreline stroked at the resolved lineweight.
 */

import type { LwpolylineEntity } from "@dxf-ai-editor/drawing-model";

import { bulgeToArc } from "../../../scene3d/adapters/arcEllipseSpline";
import type {
  CadConvertContext,
  CadPrimitive,
  PathSegment,
  Point2,
} from "../cadPrimitive";
import { IDENTITY } from "./affine2d";
import { circularArcToBeziers } from "./arcBezier";
import { resolvePlotStyle } from "./resolveStyle";
import { bandPathPrimitive, lwpolylineBand } from "./widePolylineBand";

/**
 * Append the segment(s) connecting vertex `a` → vertex `b` (with bulge on `a`)
 * to `segments`. A zero bulge is a straight line; a non-zero bulge is decoded
 * to an arc and emitted as beziers.
 */
function appendSpan(
  segments: PathSegment[],
  a: Point2,
  b: Point2,
  bulge: number,
): void {
  if (bulge === 0) {
    segments.push({ kind: "line", to: b });
    return;
  }
  const arc = bulgeToArc(a.x, a.y, b.x, b.y, bulge);
  if (!arc) {
    segments.push({ kind: "line", to: b });
    return;
  }
  const { segments: bez } = circularArcToBeziers(
    arc.centerX,
    arc.centerY,
    arc.radius,
    arc.startAngle,
    arc.endAngle,
    arc.ccw,
  );
  for (const s of bez) segments.push(s);
}

export function polylineToPrimitives(
  entity: LwpolylineEntity,
  ctx: CadConvertContext,
): CadPrimitive[] {
  const verts = entity.vertices;
  if (verts.length < 2) return [];
  const style = resolvePlotStyle(entity, ctx);
  // PDF-6.3b — CTB/STB do-not-plot honour.
  if (style.skip) return [];
  const layer = entity.layer ?? "0";

  // A wide polyline plots as its band, not as a hairline down its middle.
  // IDENTITY: a top-level LWPOLYLINE's vertices are already the plot frame
  // (this converter applies no OCS lift to the centreline either).
  const band = lwpolylineBand(entity, IDENTITY);
  // The band is FILLED or merely OUTLINED per the drawing's `$FILLMODE`
  // (`ctx.fillmode`); the geometry is the same either way.
  if (band) {
    return [bandPathPrimitive(entity.id, layer, band, style, ctx.fillmode)];
  }

  const start: Point2 = { x: verts[0]!.x, y: verts[0]!.y };
  const segments: PathSegment[] = [];

  for (let i = 0; i < verts.length - 1; i++) {
    const a = verts[i]!;
    const b = verts[i + 1]!;
    appendSpan(segments, { x: a.x, y: a.y }, { x: b.x, y: b.y }, a.bulge);
  }

  if (entity.closed && verts.length >= 2) {
    const last = verts[verts.length - 1]!;
    const first = verts[0]!;
    appendSpan(
      segments,
      { x: last.x, y: last.y },
      { x: first.x, y: first.y },
      last.bulge,
    );
  }

  return [
    {
      type: "path",
      entityId: entity.id,
      layer,
      start,
      segments,
      closed: entity.closed,
      fill: "none",
      style,
    },
  ];
}
